import { DataService } from "@/services/data";
import { PaginatedResponse, ResultResponse } from "@/types/common";
import { AnyDRQQuery, DRQBuilder, serializeDRQQuery } from "@/utils/drq";
import { useCallback, useEffect, useState, useRef } from "react";
import { FilterInstance } from "./use-object-list-filters";
import type { FilterTree } from "@/components/layout-2/filter-tree/types";
import type { ListViewOrderBy } from "@/services/list-view";

export function useDataFetching<T>(
    endpointUrl: string,
    visibleColumns: string[],
    currentDRQQuery: AnyDRQQuery | {},
    autoFetch: boolean = true,
    /** When true, list/count requests are skipped (e.g. views loading or current view not selected yet). */
    blockListFetch: boolean,
    orderBy?: ListViewOrderBy | null,
    searchValue?: string,
    drqFilterState?: FilterInstance[],
    filterLogic?: string,
    /** When visibleColumns is not yet synced, use these for the API fields param so the first fetch returns full row data */
    defaultColumns?: string[],
    pageSizeOverride?: number,
    /**
     * Send `currentDRQQuery` verbatim instead of re-deriving it from filter instances. Required for queries
     * built outside the filter UI: the resolver only knows the lookups the UI can produce, so it rewrites
     * anything else (`id__in` becomes an `equals` on a joined string).
     */
    skipDrqResolution?: boolean,
    /** Changing this forces a refetch even when every other input is identical (used by Refresh). */
    fetchNonce?: number | string,
    /** Advanced filters, sent as is in the JSON body: the server compiles them, relative dates included. */
    filterTree?: FilterTree | null
) {
    const pageSize = pageSizeOverride ?? 25;
    const [count, setCount] = useState<number | null>(null);
    const [data, setData] = useState<ResultResponse<T> | null>(null);
    const [isLoading, setIsLoading] = useState(autoFetch);
    const [isLoadingCount, setIsLoadingCount] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [accumulatedResults, setAccumulatedResults] = useState<T[]>([]);
    const [error, setError] = useState<{ status?: number; message?: string } | null>(null);
    const [drqQuery, setDrqQuery] = useState<string | null>(serializeDRQQuery(currentDRQQuery));
    const lastFetchKeyRef = useRef<string>("");
    const latestDataRequestIdRef = useRef(0);
    const latestCountRequestIdRef = useRef(0);

    const buildApiRequest = useCallback(
        (page: number = 1, drqQuery: AnyDRQQuery | {} = {}, count: boolean = false) => {
            const params = new URLSearchParams();

            // Always run through API resolution so relative dates ("today", …) become concrete bounds,
            // including when drqFilterState is briefly empty but currentDRQQuery still holds a composite.
            const queryForAPI = skipDrqResolution ? drqQuery : DRQBuilder.resolveDrqQueryForApi(drqQuery, drqFilterState, filterLogic);

            if (queryForAPI && Object.keys(queryForAPI).length > 0) {
                const serializedQuery = serializeDRQQuery(queryForAPI);
                if (serializedQuery) {
                    setDrqQuery(serializedQuery);
                    params.set("query", serializedQuery);
                }
            } else {
                // A view with no classic filters (advanced tree, or filters just cleared) must not
                // keep the previous view's query: select-all sends `drqQuery` with the action.
                setDrqQuery(null);
            }

            // The tree rides in the body. On the query string it is repeated by the CORS preflight
            // and gunicorn refuses a request line over 4094 bytes.
            const bodyTree = filterTree && filterTree.root.children.length > 0 ? filterTree : null;

            if (searchValue && searchValue.trim()) {
                params.set("search", searchValue.trim());
            } else {
                params.delete("search");
            }

            // Use visibleColumns, else defaultColumns (from metadata), else ["id"] so we always build a valid URL and the first fetch returns full row data
            const fieldsToRequest =
                visibleColumns.length > 0 ? [...new Set(["id", ...visibleColumns])] : defaultColumns && defaultColumns.length > 0 ? [...new Set(["id", ...defaultColumns])] : ["id"];
            params.set("fields", fieldsToRequest.join(","));

            if (orderBy) {
                const orderValue = orderBy.direction === "desc" ? `-${orderBy.column}` : orderBy.column;
                params.set("ordering", orderValue);
                // A sort by distance names where it is measured from.
                if (orderBy.origin) params.set("ordering_origin", JSON.stringify(orderBy.origin));
            }

            if (page > 1) {
                params.set("page", page.toString());
            }
            params.set("page_size", pageSize.toString());

            const path = count ? `${endpointUrl}count/` : bodyTree ? `${endpointUrl}filtered/` : endpointUrl;
            const url = `${path}${params.toString() ? "?" + params.toString() : ""}`;
            return { url, bodyTree };
        },
        [endpointUrl, visibleColumns, defaultColumns, pageSize, orderBy, searchValue, drqFilterState, filterLogic, skipDrqResolution, filterTree]
    );

    const fetchData = useCallback(
        async (page: number = 1, drqQuery: AnyDRQQuery | {} = currentDRQQuery) => {
            if (!autoFetch || blockListFetch) return;

            const { url, bodyTree } = buildApiRequest(page, drqQuery);

            if (!url) return;

            // Avoid duplicate requests for the same parameters (only for page 1)
            if (page === 1) {
                setIsLoading(true);
            } else {
                setIsLoadingMore(true);
            }
            const requestId = ++latestDataRequestIdRef.current;

            try {
                setError(null);

                const request = DataService.client.url(url);
                const responseData = (await (bodyTree ? request.post({ filter_tree: bodyTree }) : request.get()).json()) as PaginatedResponse<T>;
                if (requestId !== latestDataRequestIdRef.current) {
                    return;
                }

                if (page === 1) {
                    setData(responseData);
                    setAccumulatedResults(responseData.results || []);
                    setCurrentPage(1);
                } else {
                    setData(prev =>
                        prev
                            ? {
                                  ...responseData,
                                  results: [...(prev.results || []), ...(responseData.results || [])]
                              }
                            : responseData
                    );
                    setAccumulatedResults(prev => [...prev, ...(responseData.results || [])]);
                    setCurrentPage(page);
                }
            } catch (error: any) {
                if (requestId !== latestDataRequestIdRef.current) {
                    return;
                }
                console.error("Error fetching data:", error);
                // Capture l'erreur HTTP
                if (error.response) {
                    setError({
                        status: error.response.status,
                        message: error.message || "Une erreur est survenue"
                    });
                } else {
                    setError({
                        message: error.message || "Une erreur est survenue"
                    });
                }
            } finally {
                if (requestId === latestDataRequestIdRef.current) {
                    setIsLoading(false);
                    setIsLoadingMore(false);
                }
            }
        },
        [autoFetch, buildApiRequest, currentDRQQuery, blockListFetch]
    );

    const fetchCount = useCallback(
        async (drqQuery: AnyDRQQuery | {} = currentDRQQuery) => {
            if (!autoFetch || blockListFetch) return;
            setIsLoadingCount(true);
            const requestId = ++latestCountRequestIdRef.current;

            const { url, bodyTree } = buildApiRequest(1, drqQuery, true);

            if (!url) return;

            try {
                setError(null);

                const request = DataService.client.url(url);
                const responseData = (await (bodyTree ? request.post({ filter_tree: bodyTree }) : request.get()).json()) as { count: number };
                if (requestId !== latestCountRequestIdRef.current) {
                    return;
                }
                setCount(responseData.count);
            } catch (error: any) {
                if (requestId !== latestCountRequestIdRef.current) {
                    return;
                }
                console.error("Error fetching count:", error);
                // Capture l'erreur HTTP
                if (error.response) {
                    setError({
                        status: error.response.status,
                        message: error.message || "Une erreur est survenue"
                    });
                } else {
                    setError({
                        message: error.message || "Une erreur est survenue"
                    });
                }
            } finally {
                if (requestId === latestCountRequestIdRef.current) {
                    setIsLoadingCount(false);
                }
            }
        },
        [autoFetch, buildApiRequest, currentDRQQuery, blockListFetch]
    );

    useEffect(() => {
        // Debounce so rapid successive updates (view → filters → columns) coalesce into one list + count request.
        const fetchKey = JSON.stringify({
            visibleColumns,
            currentDRQQuery,
            orderBy,
            searchValue,
            autoFetch,
            blockListFetch,
            drqFilterState: drqFilterState ? JSON.stringify(drqFilterState) : "",
            filterLogic: filterLogic || "",
            pageSizeOverride: pageSizeOverride ?? 25,
            fetchNonce: fetchNonce ?? "",
            filterTree: filterTree ? JSON.stringify(filterTree) : ""
        });

        if (!autoFetch || blockListFetch) {
            return;
        }

        const t = window.setTimeout(() => {
            if (fetchKey !== lastFetchKeyRef.current) {
                lastFetchKeyRef.current = fetchKey;
                fetchData(1, currentDRQQuery);
                fetchCount(currentDRQQuery);
            }
        }, 50);

        return () => window.clearTimeout(t);
    }, [visibleColumns, currentDRQQuery, orderBy, searchValue, autoFetch, blockListFetch, drqFilterState, filterLogic, pageSizeOverride, fetchNonce, filterTree, fetchData, fetchCount]);

    const handleLoadMore = useCallback(async () => {
        if (!autoFetch || blockListFetch || isLoadingMore || isLoading || !data) return;

        const nextPage = currentPage + 1;
        const totalPages = Math.ceil((count || 0) / pageSize);

        if (nextPage <= totalPages) {
            await fetchData(nextPage, currentDRQQuery);
        }
    }, [autoFetch, blockListFetch, isLoadingMore, isLoading, data, count, currentPage, pageSize, fetchData, currentDRQQuery]);

    const handlePageReset = useCallback(() => {
        if (!autoFetch) return;
        setCurrentPage(1);
        setAccumulatedResults([]);
        fetchData(1, currentDRQQuery);
        fetchCount(currentDRQQuery);
    }, [autoFetch, fetchData, currentDRQQuery]);

    const resetAccumulatedResults = useCallback(() => {
        setAccumulatedResults([]);
    }, []);

    return {
        data,
        count,
        isLoading,
        isLoadingCount,
        currentPage,
        pageSize,
        isLoadingMore,
        accumulatedResults,
        error,
        drqQuery,
        fetchData,
        handleLoadMore,
        handlePageReset,
        resetAccumulatedResults,
        setAccumulatedResults,
        setCurrentPage
    };
}
